import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import LoadingSpinner from './LoadingSpinner';
import './LearningContextPreview.css';

const DEFAULT_STAGES = ['Course', 'Unit', 'Objective', 'Activity'];

const LearningContextPreview = ({
  courseTitle,
  lessonTitle,
  lessonObjective,
  courseObjectives = [],
  activityLabel = 'Activity',
  eyebrow = 'Preparing your learning context',
  subtitle = "We're grounding this lesson in your current course path before the activity starts.",
  loadingMessage = 'Building activities for this lesson...',
  className = '',
}) => {
  const stages = ['Course', 'Unit', 'Objective', activityLabel];
  const [activeStageIndex, setActiveStageIndex] = useState(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveStageIndex((current) => (current + 1) % stages.length);
    }, 1600);

    return () => window.clearInterval(intervalId);
  }, [stages.length]);

  return (
    <div className={`learning-context-preview ${className}`.trim()} aria-live="polite">
      <div className="learning-context-preview__hero">
        <span className="learning-context-preview__eyebrow">{eyebrow}</span>
        <h2 className="learning-context-preview__title">{lessonTitle}</h2>
        <p className="learning-context-preview__subtitle">{subtitle}</p>
      </div>

      <div className="learning-context-preview__stages" role="list" aria-label="Lesson setup steps">
        {stages.map((stage, index) => (
          <div
            key={stage}
            role="listitem"
            className={`learning-context-preview__stage ${
              index === activeStageIndex ? 'is-active' : ''
            } ${index < activeStageIndex ? 'is-complete' : ''}`}
          >
            <span className="learning-context-preview__stage-index">{index + 1}</span>
            <span className="learning-context-preview__stage-label">{stage}</span>
          </div>
        ))}
      </div>

      <div className="learning-context-preview__grid">
        <div className="learning-context-preview__card">
          <span className="learning-context-preview__label">Course</span>
          <h3 className="learning-context-preview__value">{courseTitle}</h3>
        </div>

        <div className="learning-context-preview__card">
          <span className="learning-context-preview__label">Unit</span>
          <h3 className="learning-context-preview__value">{lessonTitle}</h3>
        </div>

        <div className="learning-context-preview__card learning-context-preview__card--wide">
          <span className="learning-context-preview__label">Unit Objective</span>
          <p className="learning-context-preview__objective">{lessonObjective}</p>
        </div>
      </div>

      {courseObjectives.length > 0 ? (
        <div className="learning-context-preview__objectives">
          <span className="learning-context-preview__label">Course Objectives</span>
          <div className="learning-context-preview__chips">
            {courseObjectives.slice(0, 6).map((objective) => (
              <span key={objective} className="learning-context-preview__chip">
                {objective}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <div className="learning-context-preview__loading">
        <LoadingSpinner message={loadingMessage} />
      </div>
    </div>
  );
};

LearningContextPreview.propTypes = {
  activityLabel: PropTypes.string,
  className: PropTypes.string,
  courseObjectives: PropTypes.arrayOf(PropTypes.string),
  courseTitle: PropTypes.string,
  eyebrow: PropTypes.string,
  lessonObjective: PropTypes.string,
  lessonTitle: PropTypes.string,
  loadingMessage: PropTypes.string,
  subtitle: PropTypes.string,
};

LearningContextPreview.defaultProps = {
  activityLabel: DEFAULT_STAGES[3],
  className: '',
  courseObjectives: [],
  courseTitle: 'Untitled Course',
  eyebrow: 'Preparing your learning context',
  lessonObjective: 'No lesson objective provided.',
  lessonTitle: 'Untitled Lesson',
  loadingMessage: 'Building activities for this lesson...',
  subtitle: "We're grounding this lesson in your current course path before the activity starts.",
};

export default LearningContextPreview;
